import { Injectable, inject, signal } from '@angular/core';
import { QueryBuilder } from '@softline/core';
import { ClaimReportSalesOrderRepository } from '../repositories/claim-report-sales-order.repository';
import {
  ClaimReportSalesOrder,
  ClaimReportSalesOrderQueryRow,
  ClaimReportSalesOrderResponseRow,
} from '../types/claim-report-document-query';

const PAGE_SIZE = 50;

@Injectable()
export class ClaimReportSalesOrderService {
  private readonly repository = inject(ClaimReportSalesOrderRepository);

  readonly salesOrders = signal<ClaimReportSalesOrder[]>([]);
  readonly selectedSalesOrder = signal<ClaimReportSalesOrder | null>(null);
  readonly isLoading = signal(false);
  readonly isLoadingMore = signal(false);
  readonly hasMore = signal(false);
  readonly error = signal<unknown>(null);

  private generation = 0;
  private offset = 0;
  private customerNumber: number | null = null;

  async load(customerNumber: number): Promise<void> {
    const generation = ++this.generation;
    this.customerNumber = customerNumber;
    this.offset = 0;
    this.hasMore.set(false);
    this.isLoadingMore.set(false);
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const page = await this.fetchPage({ customerNumber, start: 0 });
      if (generation !== this.generation) return;
      this.salesOrders.set(page);
      this.offset = page.length;
      this.hasMore.set(page.length === PAGE_SIZE);
    } catch (e) {
      if (generation === this.generation) this.error.set(e);
      throw e;
    } finally {
      if (generation === this.generation) this.isLoading.set(false);
    }
  }

  async loadMore(): Promise<void> {
    if (this.isLoading() || this.isLoadingMore() || !this.hasMore()) return;
    if (this.customerNumber === null) return;
    const generation = this.generation;
    this.isLoadingMore.set(true);
    try {
      const page = await this.fetchPage({
        customerNumber: this.customerNumber,
        start: this.offset,
      });
      if (generation !== this.generation) return;
      this.salesOrders.update((current) => [...current, ...page]);
      this.offset += page.length;
      this.hasMore.set(page.length === PAGE_SIZE);
    } finally {
      if (generation === this.generation) this.isLoadingMore.set(false);
    }
  }

  async loadSalesOrder(documentId: string): Promise<void> {
    const [order] = await this.fetchPage({ documentId, start: 0 });
    this.selectedSalesOrder.set(order ?? null);
  }

  async findCustomerNumberByBarcode(barcode: string): Promise<number | null> {
    const query = new QueryBuilder<ClaimReportSalesOrderQueryRow>()
      .column('sales_order.id')
      .column('invoice_customer.number')
      .filter({ type: 'stringEquals', name: 'sales_order.sale_department', value: 'V' })
      .filter({ type: 'stringEquals', name: 'sales_order.barcode', value: barcode })
      .start(0)
      .end(1)
      .build();
    const result = await this.repository.load({ query });
    const rows = (result?.data ?? []) as unknown as ClaimReportSalesOrderResponseRow[];
    return rows[0]?.invoice_customer.number || null;
  }

  private async fetchPage(options: {
    customerNumber?: number;
    documentId?: string;
    start: number;
  }): Promise<ClaimReportSalesOrder[]> {
    const builder = new QueryBuilder<ClaimReportSalesOrderQueryRow>()
      .column('sales_order.id')
      .column('sales_order.document_date')
      .sort('sales_order.document_date', 'DESC')
      .sort('sales_order.id', 'DESC')
      .filter({ type: 'stringEquals', name: 'sales_order.sale_department', value: 'V' });
    if (options.customerNumber !== undefined)
      builder.filter({
        type: 'numberEquals',
        name: 'invoice_customer.number',
        value: options.customerNumber,
      });
    if (options.documentId !== undefined)
      builder.filter({
        type: 'stringEquals',
        name: 'sales_order.id',
        value: options.documentId.slice(options.documentId.lastIndexOf('_') + 1),
      });
    const size = options.documentId !== undefined ? 1 : PAGE_SIZE;
    const query = builder
      .start(options.start)
      .end(options.start + size)
      .build();
    const result = await this.repository.load({ query });
    const rows = (result?.data ?? []) as unknown as ClaimReportSalesOrderResponseRow[];
    return rows.map((row) => ({
      id: row.sales_order.id,
      documentDate: row.sales_order.document_date,
    }));
  }
}
